import EventEmitter from "eventemitter3";
import {
  action,
  autorun,
  comparer,
  computed,
  IComputedValue,
  IObservableValue,
  makeObservable,
  observable,
  reaction,
  runInAction,
  untracked,
  when,
} from "mobx";
import { atom } from "../atom";
import { LLM_INPUT_ID } from "../Constants";
import {
  ActivePaneState,
  EvaluateExpressionType,
  LoadState,
  Pane,
  PaneInput,
  PaneOutput,
  PaneType,
  PaneViewMode,
  Rect,
  SupportedBabelPlugin,
} from "../Types";
import { areArraysEqual, assert } from "../Utils";
import {
  activePaneStates,
  canvasAtom,
  canvasLayoutIdAtom,
  canvasTransformComputed,
  canvasViewport,
  isSafeModeAtom,
  layoutComputed,
  layoutPreviewAddPaneIdBox,
  magicLayoutAtom,
  PLACEHOLDER_CANVAS_ID,
  sandboxLayoutOverrideBox,
  sidebarHoverPaneIdAtom,
  transferPaneInputSourceBox,
  urlLayoutIdAtom,
} from "./CanvasState";
import {
  Evaluator,
  getFunctionComputed,
  getPaneInputBox,
  hasLoadedGlobalsComputed,
} from "./Evaluator";
import { ParentComlinkAPI } from "./FrameSandboxAPI";
import {
  getLogInspectorValues,
  logInspectorFunctionName,
} from "./LogInspector";
import { NattoLibrary } from "./NattoLibrary";
import {
  HIDDEN_PANE_RECT,
  MAGIC_LAYOUT_ID,
  PANE_ERROR_STACK_PREFIX,
} from "./SandboxConstants";
import {
  convertViewportPositionToCanvasOffset,
  getAddLayoutPaneRect,
  getRectForCollapsedPane,
  getRectFromLayoutPane,
  getTopPaneAtPosition,
  isLLMConfigChat,
  isPaneCollapsed,
  statePaneGetterValue,
  statePaneSetterValue,
  updatePaneInputWithSource,
} from "./SandboxUtils";

export const EvaluatorRunEmitter = new EventEmitter();

function shallowBox<T>(value: T): IObservableValue<T> {
  return observable.box(value, { deep: false });
}

const consoleLog = console.log;

function createEvaluator(
  functionGet: { get(): LoadState<Function> },
  paneComputed: IComputedValue<
    | Extract<Pane, { type: PaneType.Evaluate | PaneType.EvaluateGlobal }>
    | undefined
  >,
  activePaneState: ActivePaneState,
  expressionTypeGet: { get(): EvaluateExpressionType },
  expressionGet: { get(): string },
  babelPluginIdsGet: { get(): (SupportedBabelPlugin | "log-inspector")[] },
  paneInputsComputed: IComputedValue<PaneInput[]>
) {
  const pane = paneComputed.get()!;
  const isGlobalPane = (pane.type as any) === PaneType.EvaluateGlobal;
  const inspectorLogValuesRef: {
    current: [
      number,
      Record<number, { end: number; name: string; values: any[] }>
    ];
  } = { current: [0, {}] };
  const extraArgs = {
    console: (runId: number) => ({
      ...console,
      log: action((...args: any[]) => {
        const value = args.length > 1 ? args : args[0];
        if (activePaneState.type === PaneType.Evaluate) {
          let consoleLogValues = activePaneState.consoleLogValuesBox.get();
          if (consoleLogValues === undefined && evaluator.runId === runId) {
            activePaneState.consoleLogValuesBox.set(
              makeObservable(
                { runnerId: runId, values: [value] },
                {
                  runnerId: observable,
                  values: observable.shallow,
                }
              )
            );
          } else if (consoleLogValues?.runnerId === runId) {
            consoleLogValues!.values.push(value);
          }
          consoleLog(...args);
        }
      }),
    }),
    [logInspectorFunctionName]:
      (runId: number) =>
      (v: any, options: { start: number; name: string; end: number }) => {
        const [logRunnerId, logValuesRecord] = inspectorLogValuesRef.current;
        if (logRunnerId === runId) {
          if (logValuesRecord[options.start] === undefined) {
            logValuesRecord[options.start] = {
              end: options.end,
              name: options.name,
              values: [],
            };
          }
          logValuesRecord[options.start].values.push(v);
        }
        return v;
      },
  };
  const evaluator = new Evaluator(
    functionGet,
    activePaneState.outputBoxes[0],
    isGlobalPane,
    paneInputsComputed,
    (paneInput) => getPaneInputBox(paneInput),
    babelPluginIdsGet,
    computed(
      () =>
        paneComputed.get()?.autorun === true &&
        !isSafeModeAtom.value &&
        (isGlobalPane || hasLoadedGlobalsComputed.get())
    ),
    activePaneState.type === PaneType.Evaluate
      ? activePaneState.isTryCatchDisabledBox
      : undefined,
    extraArgs,
    action((runId) => {
      if (activePaneState.type === PaneType.Evaluate) {
        activePaneState.consoleLogValuesBox.set(undefined);
      }
      inspectorLogValuesRef.current = [runId, {}];
    }),
    action((runId) => {
      const outputOfPaneInputs = evaluator.paneInputsGet
        .get()
        .map((paneInput) => getPaneInputBox(paneInput)?.get());
      if (activePaneState.type === PaneType.Evaluate) {
        activePaneState.outputDepsBox.set([
          expressionGet.get(),
          outputOfPaneInputs,
          expressionTypeGet.get(),
        ]);
      }
      if (activePaneState.type === PaneType.Evaluate) {
        activePaneState.inspectorLogValuesBox.set(
          Object.keys(inspectorLogValuesRef.current[1]).length > 0
            ? Object.entries(inspectorLogValuesRef.current[1]).map(
                ([from, { end, name, values }]) => [
                  parseInt(from),
                  end,
                  name,
                  values,
                ]
              )
            : undefined
        );
      }
    })
  );
  return evaluator;
}

function setupEvalPane(
  paneComputed: IComputedValue<
    | Extract<Pane, { type: PaneType.Evaluate | PaneType.EvaluateGlobal }>
    | undefined
  >,
  rectComputed: IComputedValue<Rect>,
  zIndexComputed: IComputedValue<number>,
  viewModeComputed: IComputedValue<PaneViewMode>,
  editorHeightComputed: IComputedValue<number | undefined>
): [ActivePaneState, (() => void)[]] {
  const pane = paneComputed.get()!;
  const paneInputsComputed = computed<PaneInput[]>(
    () => {
      const pane = paneComputed.get();
      return pane !== undefined && "inputs" in pane ? pane.inputs : [];
    },
    { equals: comparer.structural }
  );
  const expressionTypeGet = computed(() => {
    const pane = paneComputed.get();
    return (
      (pane?.type === PaneType.Evaluate ? pane.expressionType : undefined) ??
      EvaluateExpressionType.Expression
    );
  });
  const expressionBox = observable.box(pane.expression);
  const isLogInspectorEnabledBox = observable.box(false);
  const babelPluginIdsGet = computed<
    (SupportedBabelPlugin | "log-inspector")[]
  >(
    () => {
      const pane = paneComputed.get();
      const paneBabelPlugins =
        (pane?.type === PaneType.Evaluate ? pane.babelPlugins : undefined) ??
        [];
      return [
        ...paneBabelPlugins,
        ...(isLogInspectorEnabledBox.get() ? ["log-inspector" as const] : []),
      ];
    },
    { equals: comparer.structural }
  );
  const functionGet = getFunctionComputed(
    false,
    expressionTypeGet,
    expressionBox,
    paneInputsComputed,
    babelPluginIdsGet,
    ["console", logInspectorFunctionName],
    `${PANE_ERROR_STACK_PREFIX}${pane.id}`
  );

  const activePaneState: ActivePaneState =
    pane.type === PaneType.EvaluateGlobal
      ? {
          type: PaneType.EvaluateGlobal,
          rect: rectComputed,
          zIndex: zIndexComputed,
          viewMode: viewModeComputed,
          outputBoxes: [shallowBox<PaneOutput>(["waiting"])],
          editorHeight: editorHeightComputed,
        }
      : {
          type: PaneType.Evaluate,
          rect: rectComputed,
          zIndex: zIndexComputed,
          viewMode: viewModeComputed,
          functionGet,
          outputBoxes: [shallowBox<PaneOutput>(["waiting"])],
          editorHeight: editorHeightComputed,
          isTryCatchDisabledBox: observable.box(false),
          isLogInspectorEnabledBox,
          consoleLogValuesBox: observable.box(undefined, { deep: false }),
          inspectorLogValuesBox: observable.box(undefined, {
            deep: false,
          }),
          outputDepsBox: observable.box(
            ["", undefined, EvaluateExpressionType.Expression],
            { deep: false }
          ),
        };
  activePaneStates.set(pane.id, activePaneState);
  const evalPaneComputed: IComputedValue<
    | Extract<Pane, { type: PaneType.Evaluate | PaneType.EvaluateGlobal }>
    | undefined
  > = paneComputed as any;
  const evaluator = createEvaluator(
    functionGet,
    evalPaneComputed,
    activePaneState,
    expressionTypeGet,
    expressionBox,
    babelPluginIdsGet,
    paneInputsComputed
  );
  function callback() {
    runInAction(() => {
      const expression = evalPaneComputed.get()?.expression ?? "";
      expressionBox.set(expression);
    });
    evaluator.run();
  }
  EvaluatorRunEmitter.addListener(`${pane.id}`, callback);
  return [
    activePaneState,
    [
      evaluator.init(),
      reaction(
        () => evalPaneComputed.get()?.expression ?? "",
        action((_expression) => {
          const expression = evalPaneComputed.get()?.expression ?? "";
          expressionBox.set(expression);
        }),
        { delay: 80 }
      ),
      reaction(
        () => paneInputsComputed.get(),
        action((_paneInputs) => {
          // handle semantic rename of input where we want to update the
          // expression immediately without waiting for throttle
          const expression = evalPaneComputed.get()?.expression ?? "";
          expressionBox.set(expression);
        })
      ),
      ...(activePaneState.type === PaneType.Evaluate
        ? [
            reaction(
              () => activePaneState.isLogInspectorEnabledBox.get(),
              (isLogInspectorEnabled) => {
                if (!isLogInspectorEnabled) {
                  activePaneState.inspectorLogValuesBox.set(undefined);
                }
              }
            ),
          ]
        : []),
      () => {
        EvaluatorRunEmitter.removeListener(`${pane.id}`, callback);
      },
    ],
  ];
}

function setupTransferPaneInputSourceEffect() {
  const dragShield = document.createElement("div");
  dragShield.className = "fixed top-0 left-0 right-0 bottom-0 z-99999";
  let cleanupTransferPaneInputSource: (() => void) | undefined;
  reaction(
    () => transferPaneInputSourceBox.get() !== undefined,
    (isTransferring) => {
      if (isTransferring) {
        document.body.appendChild(dragShield);
        function onMouseMove(e: MouseEvent) {
          runInAction(() => {
            const transferInputSource = transferPaneInputSourceBox.get();
            if (transferInputSource !== undefined) {
              const position = convertViewportPositionToCanvasOffset(
                [e.clientX, e.clientY],
                canvasTransformComputed.get(),
                canvasViewport.zoom
              );
              transferInputSource.position = position;
              const hoverPane = getTopPaneAtPosition(
                canvasAtom.value.panes,
                position
              );
              transferInputSource.hoverPaneOutput =
                hoverPane !== undefined ? [hoverPane.id, 0] : undefined;
            }
          });
        }
        const cursorAutorunCleanup = autorun(() => {
          dragShield.style.cursor = computed(
            () =>
              transferPaneInputSourceBox.get()?.hoverPaneOutput !== undefined
          ).get()
            ? "pointer"
            : "grabbing";
        });
        function cleanup() {
          document.body.removeChild(dragShield);
          window.removeEventListener("mousemove", onMouseMove);
          window.removeEventListener("click", onClick);
          runInAction(() => {
            transferPaneInputSourceBox.set(undefined);
          });
          cursorAutorunCleanup();
          cleanupTransferPaneInputSource = undefined;
        }
        cleanupTransferPaneInputSource = cleanup;
        function onClick(e: MouseEvent) {
          const transferInputSource = transferPaneInputSourceBox.get();
          if (transferInputSource !== undefined) {
            if (
              transferInputSource.hoverPaneOutput !== undefined &&
              !areArraysEqual(
                transferInputSource.hoverPaneOutput,
                transferInputSource.originalPaneOutput
              )
            ) {
              for (const paneInputGlobalKey of transferInputSource.paneInputs) {
                updatePaneInputWithSource(
                  paneInputGlobalKey,
                  transferInputSource.hoverPaneOutput
                );
              }
            }
          }
          cleanup();
        }
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("click", onClick);
      } else if (cleanupTransferPaneInputSource !== undefined) {
        cleanupTransferPaneInputSource();
      }
    }
  );
}

export function setupCanvasStateEffects() {
  reaction<[string | undefined, boolean, boolean], boolean>(
    () => [
      urlLayoutIdAtom.value,
      canvasAtom.value.id === PLACEHOLDER_CANVAS_ID,
      magicLayoutAtom.value !== undefined,
    ],
    ([urlLayoutId, isCanvasLoading, hasMagicLayout]) => {
      if (urlLayoutId === MAGIC_LAYOUT_ID) {
        if (!hasMagicLayout) {
          return;
        }
      } else if (urlLayoutId !== undefined) {
        if (isCanvasLoading) {
          return;
        }
        if (!canvasAtom.value.layouts.some((l) => l.id === urlLayoutId)) {
          ParentComlinkAPI.switchLayoutId(undefined);
          return;
        }
      }
      runInAction(() => {
        canvasLayoutIdAtom.value = urlLayoutId;
      });
    },
    { equals: comparer.structural, fireImmediately: true }
  );

  const paneUnsubscribes: Record<string, (() => void)[]> = {};
  reaction(
    () => canvasAtom.value.panes.map((p) => p.id),
    action((paneIds) => {
      canvasAtom.value.panes.forEach((pane) => {
        const paneId = pane.id;
        if (!activePaneStates.has(paneId)) {
          const layoutPaneComputed = computed(() =>
            layoutComputed.get()?.panes.find((p) => p.id === paneId)
          );
          const paneComputed = computed(() =>
            canvasAtom.value.panes.find((p) => p.id === paneId)
          );
          const rectComputed = computed<Rect>(
            () => {
              if (layoutPreviewAddPaneIdBox.get() === paneId) {
                return getAddLayoutPaneRect(paneId);
              }
              if (canvasLayoutIdAtom.value !== undefined) {
                const layoutPane = layoutPaneComputed.get();
                return layoutPane !== undefined
                  ? getRectFromLayoutPane(layoutPane)
                  : HIDDEN_PANE_RECT;
              }
              const sandboxLayoutOverride = sandboxLayoutOverrideBox.get();
              const pane = paneComputed.get();
              const rect = sandboxLayoutOverride?.[paneId]?.rect ?? pane?.rect;
              if (
                rect !== undefined &&
                pane !== undefined &&
                (sandboxLayoutOverride !== undefined
                  ? sandboxLayoutOverride[paneId]?.collapsed === true
                  : isPaneCollapsed(pane))
              ) {
                return getRectForCollapsedPane(rect, pane.type, pane.name);
              }
              return rect ?? HIDDEN_PANE_RECT;
            },
            { equals: comparer.structural }
          );
          const zIndexComputed = computed(() => {
            const sandboxLayoutOverride = sandboxLayoutOverrideBox.get();
            if (sandboxLayoutOverride?.[paneId] !== undefined) {
              return sandboxLayoutOverride[paneId].z;
            }
            return (
              (canvasLayoutIdAtom.value !== undefined
                ? layoutPaneComputed.get()?.z
                : paneComputed.get()?.z) ?? -1
            );
          });
          const viewModeComputed = computed(() => {
            const sandboxLayoutOverride = sandboxLayoutOverrideBox.get();
            if (sandboxLayoutOverride?.[paneId] !== undefined) {
              return sandboxLayoutOverride[paneId].collapsed === true
                ? PaneViewMode.Collapsed
                : PaneViewMode.Normal;
            }
            return layoutPaneComputed.get() !== undefined
              ? PaneViewMode.Normal
              : canvasLayoutIdAtom.value !== undefined
              ? PaneViewMode.Hidden
              : isPaneCollapsed(paneComputed.get())
              ? PaneViewMode.Collapsed
              : PaneViewMode.Normal;
          });
          const editorHeightComputed = computed<number | undefined>(() => {
            if (sidebarHoverPaneIdAtom.value === paneId) {
              return 0;
            }
            const sandboxLayoutOverride = sandboxLayoutOverrideBox.get();
            if (sandboxLayoutOverride?.[paneId] !== undefined) {
              return sandboxLayoutOverride[paneId].editorHeight;
            }
            return layoutPreviewAddPaneIdBox.get() === paneId
              ? getAddLayoutPaneRect(paneId)
              : canvasLayoutIdAtom.value !== undefined
              ? layoutPaneComputed.get()?.editorHeight
              : (paneComputed.get() as any)?.editorHeight;
          });
          switch (pane.type) {
            case PaneType.State: {
              const value = getLogInspectorValues(pane.id);
              const outputBox = shallowBox<PaneOutput>(
                value !== undefined ? ["value", value] : ["waiting"]
              );
              activePaneStates.set(pane.id, {
                type: pane.type,
                rect: rectComputed,
                zIndex: zIndexComputed,
                viewMode: viewModeComputed,
                outputBoxes: [
                  outputBox,
                  shallowBox<PaneOutput>(statePaneSetterValue(outputBox)),
                  shallowBox<PaneOutput>(statePaneGetterValue(outputBox)),
                ],
                editorHeight: editorHeightComputed,
              });
              paneUnsubscribes[pane.id] = [
                reaction(
                  () =>
                    (
                      paneComputed as IComputedValue<
                        Extract<Pane, { type: PaneType.State }> | undefined
                      >
                    ).get()?.initialExpression,
                  action((initialExpression) => {
                    if (initialExpression === undefined) {
                      return;
                    }
                    if (initialExpression === "") {
                      if (outputBox.get()[0] === "value") {
                        outputBox.set(["waiting"]);
                      }
                    } else {
                      let output: PaneOutput;
                      try {
                        const f = new Function(`return ${initialExpression}`);
                        output = ["value", f()];
                      } catch (e) {
                        output = ["error", e];
                      }
                      outputBox.set(output);
                    }
                  }),
                  { fireImmediately: true }
                ),
              ];
              break;
            }
            case PaneType.Evaluate: {
              const [activePaneState, paneUnsubscribe] = setupEvalPane(
                paneComputed as any,
                rectComputed,
                zIndexComputed,
                viewModeComputed,
                editorHeightComputed
              );
              activePaneStates.set(pane.id, activePaneState);
              paneUnsubscribes[pane.id] = paneUnsubscribe;
              break;
            }
            case PaneType.EvaluateGlobal: {
              const [activePaneState, paneUnsubscribe] = setupEvalPane(
                paneComputed as any,
                rectComputed,
                zIndexComputed,
                viewModeComputed,
                editorHeightComputed
              );
              activePaneStates.set(pane.id, activePaneState);
              paneUnsubscribes[pane.id] = paneUnsubscribe;
              break;
            }
            case PaneType.EvaluateTemplate: {
              activePaneStates.set(pane.id, {
                type: pane.type,
                rect: rectComputed,
                zIndex: zIndexComputed,
                viewMode: viewModeComputed,
                outputBoxes: [shallowBox<PaneOutput>(["waiting"])],
                editorHeight: editorHeightComputed,
                paneInputs: atom(undefined),
              });
              break;
            }
            case PaneType.InputText: {
              activePaneStates.set(pane.id, {
                type: pane.type,
                rect: rectComputed,
                zIndex: zIndexComputed,
                viewMode: viewModeComputed,
                outputBoxes: [shallowBox<PaneOutput>(["value", pane.text])],
              });
              break;
            }
            case PaneType.Import: {
              activePaneStates.set(pane.id, {
                type: pane.type,
                rect: rectComputed,
                zIndex: zIndexComputed,
                viewMode: viewModeComputed,
                outputBoxes: [shallowBox<PaneOutput>(["waiting"])],
              });
              break;
            }
            case PaneType.EnvironmentVariable: {
              activePaneStates.set(pane.id, {
                type: pane.type,
                rect: rectComputed,
                zIndex: zIndexComputed,
                viewMode: viewModeComputed,
                outputBoxes: [shallowBox<PaneOutput>(["waiting"])],
              });
              break;
            }
            case PaneType.Library: {
              activePaneStates.set(pane.id, {
                type: pane.type,
                rect: rectComputed,
                zIndex: zIndexComputed,
                viewMode: viewModeComputed,
                outputBoxes: [shallowBox<PaneOutput>(["value", NattoLibrary])],
              });
              break;
            }
            case PaneType.LLM: {
              activePaneStates.set(pane.id, {
                type: pane.type,
                rect: rectComputed,
                zIndex: zIndexComputed,
                viewMode: viewModeComputed,
                outputBoxes: [shallowBox<PaneOutput>(["waiting"])],
                paneInputsComputed: computed(
                  () => {
                    const pane = paneComputed.get();
                    assert(pane?.type === PaneType.LLM);
                    return [
                      {
                        id: LLM_INPUT_ID,
                        name: isLLMConfigChat(pane.config)
                          ? "messages"
                          : "prompt",
                        source:
                          pane?.type === PaneType.LLM
                            ? pane.inputSource
                            : undefined,
                      },
                    ];
                  },
                  { equals: comparer.structural }
                ),
                editorHeight: editorHeightComputed,
              });
              break;
            }
            case PaneType.Function: {
              const outputBox = computed((): PaneOutput => {
                const paneIds = computed(
                  () =>
                    (
                      paneComputed as IComputedValue<
                        Extract<Pane, { type: PaneType.Function }> | undefined
                      >
                    ).get()?.paneIds
                ).get();
                if (paneIds === undefined) {
                  return ["error", "cannot get panes for function"];
                }
                const dependencyError = computed<string | undefined>(() => {
                  for (const paneId of paneIds) {
                    const pane = canvasAtom.value.panes.find(
                      (p) => p.id === paneId
                    );
                    if (pane?.type === PaneType.InputText) {
                      continue;
                    }
                    if (pane?.type !== PaneType.Evaluate) {
                      return `A connected pane is missing. Please recreate this function pane.`;
                    }
                    const activePaneState = activePaneStates.get(paneId);
                    if (activePaneState?.type !== PaneType.Evaluate) {
                      return `connected pane missing`;
                    }
                  }
                }).get();
                if (dependencyError !== undefined) {
                  return ["error", dependencyError];
                }

                const inputPaneId = computed(
                  () =>
                    (
                      paneComputed as IComputedValue<
                        Extract<Pane, { type: PaneType.Function }> | undefined
                      >
                    ).get()?.inputPaneId
                ).get();
                const outputPaneId = computed(
                  () =>
                    (
                      paneComputed as IComputedValue<
                        Extract<Pane, { type: PaneType.Function }> | undefined
                      >
                    ).get()?.outputPaneId
                ).get();

                return [
                  "value",
                  async (inputValue: any) => {
                    return await untracked(async () => {
                      const functionPaneOutputs: Record<
                        string,
                        IObservableValue<PaneOutput>
                      > = Object.fromEntries(
                        paneIds.map((paneId) => {
                          const pane = canvasAtom.value.panes.find(
                            (p) => p.id === paneId
                          );
                          return [
                            paneId,
                            shallowBox<PaneOutput>(
                              paneId === inputPaneId
                                ? ["value", inputValue]
                                : pane?.type === PaneType.InputText
                                ? ["value", pane.text]
                                : ["waiting"]
                            ),
                          ];
                        })
                      );
                      let returnValue;
                      await Promise.all(
                        paneIds.map(async (paneId) => {
                          if (paneId === inputPaneId) {
                            if (paneId === outputPaneId) {
                              returnValue = inputValue;
                            }
                            return;
                          }
                          const pane = canvasAtom.value.panes.find(
                            (p) => p.id === paneId
                          );
                          if (pane?.type === PaneType.InputText) {
                            if (paneId === outputPaneId) {
                              returnValue = pane.text;
                            }
                            return;
                          }
                          if (pane?.type !== PaneType.Evaluate) {
                            throw new Error(
                              `unexpected pane type: ${pane?.type}`
                            );
                          }
                          const activePaneState = activePaneStates.get(paneId);
                          if (activePaneState?.type !== PaneType.Evaluate) {
                            throw new Error(
                              `unexpected activePaneState type: ${pane?.type}`
                            );
                          }
                          await when(
                            () =>
                              activePaneState.functionGet.get()[0] !== "loading"
                          );
                          const functionValue =
                            activePaneState.functionGet.get();
                          if (functionValue[0] !== "value") {
                            throw new Error(functionValue[1]);
                          }
                          const fn = functionValue[1];
                          const inputs = await Promise.all(
                            pane.inputs.map(async (paneInput) => {
                              if (paneInput.source === undefined) {
                                throw new Error(
                                  `pane ${pane.id} has disconnected input`
                                );
                              }
                              const inputPaneId = paneInput.source[0];
                              const outputBox =
                                functionPaneOutputs[inputPaneId] ??
                                activePaneStates.get(inputPaneId)!.outputBoxes[
                                  paneInput.source[1]
                                ];
                              await when(() =>
                                ["value", "error"].includes(outputBox.get()[0])
                              );
                              const output = outputBox.get();
                              if (output[0] !== "value") {
                                throw new Error(output[1]);
                              }
                              return output[1];
                            })
                          );
                          const rv = await fn(
                            inputs,
                            ...inputs,
                            console,
                            <T>(v: T) => v
                          );
                          runInAction(() => {
                            functionPaneOutputs[paneId].set(["value", rv]);
                          });
                          if (paneId === outputPaneId) {
                            returnValue = rv;
                          }
                        })
                      );
                      return returnValue;
                    });
                  },
                ];
              });
              activePaneStates.set(pane.id, {
                type: pane.type,
                rect: rectComputed,
                zIndex: zIndexComputed,
                viewMode: viewModeComputed,
                // @ts-ignore
                outputBoxes: [outputBox],
              });
              break;
            }
            default: {
              throw new Error();
            }
          }
        }
      });

      for (const paneId of activePaneStates.keys()) {
        if (!paneIds.includes(paneId)) {
          activePaneStates.delete(paneId);
          paneUnsubscribes[paneId]?.forEach((unsubscribe) => {
            unsubscribe();
          });
        }
      }
    }),
    { equals: comparer.structural }
  );

  reaction(
    () => canvasLayoutIdAtom.value !== undefined,
    action((hasLayout) => {
      if (hasLayout && sandboxLayoutOverrideBox.get() !== undefined) {
        sandboxLayoutOverrideBox.set(undefined);
      }
    })
  );
  reaction(
    () =>
      sandboxLayoutOverrideBox.get() !== undefined
        ? canvasAtom.value.panes.map((p) => p.id)
        : undefined,
    action((paneIds) => {
      if (paneIds !== undefined) {
        const sandboxLayoutOverride = sandboxLayoutOverrideBox.get();
        if (sandboxLayoutOverride !== undefined) {
          for (const paneId of paneIds) {
            if (sandboxLayoutOverride[paneId] === undefined) {
              const pane = canvasAtom.value.panes.find((p) => p.id === paneId)!;
              sandboxLayoutOverride[paneId] = {
                rect: pane.rect,
                z: pane.z,
                collapsed: isPaneCollapsed(pane),
                ...("editorHeight" in pane
                  ? { editorHeight: pane.editorHeight }
                  : {}),
              };
            }
          }
        }
      }
    }),
    { equals: comparer.structural }
  );

  setupTransferPaneInputSourceEffect();
}
